import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Paragraph,
  Select,
  Separator,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { useAppToast } from '@mrrmlab/ui';
import type {
  PaperlessCorrespondent,
  PaperlessDocument,
  PaperlessDocumentType,
  PaperlessTag,
  UpdateDocumentPatch,
} from '@mrrmlab/api-client';
import {
  useDocument,
  useDocumentPreviewBlob,
  usePaperlessCorrespondents,
  usePaperlessDocumentTypes,
  usePaperlessTags,
  useUpdateDocument,
} from '../archiv/hooks';
import { useDocumentsApi } from '../archiv/api-context';

export function ArchivDetailRoute() {
  const { documentId } = useParams({ from: '/archiv/$documentId' });
  const id = Number(documentId);
  const validId = Number.isFinite(id) && id > 0;
  const navigate = useNavigate();

  const { data: doc, isLoading, error } = useDocument(validId ? id : undefined);
  const { data: tagsList } = usePaperlessTags();
  const { data: correspondentsList } = usePaperlessCorrespondents();
  const { data: documentTypesList } = usePaperlessDocumentTypes();

  if (!validId) {
    return (
      <YStack padding="$4">
        <Text color="$red10">Ungültige Dokument-ID: {documentId}</Text>
      </YStack>
    );
  }

  if (isLoading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$6">
        <Spinner size="large" />
      </YStack>
    );
  }

  if (error || !doc) {
    return (
      <YStack padding="$4" gap="$3">
        <Text color="$red10">
          {error instanceof Error ? error.message : 'Dokument konnte nicht geladen werden.'}
        </Text>
        <Button onPress={() => navigate({ to: '/archiv' })}>Zurück zum Archiv</Button>
      </YStack>
    );
  }

  return (
    <YStack padding="$4" gap="$3" maxWidth={1200} width="100%">
      <XStack alignItems="center" gap="$3" flexWrap="wrap">
        <Link to="/archiv">
          <Button size="$2" chromeless>← Archiv</Button>
        </Link>
        <Text fontSize="$7" fontWeight="700" flex={1}>
          {doc.title || '(ohne Titel)'}
        </Text>
        <DownloadButton id={id} filename={doc.original_file_name ?? `${doc.title}.pdf`} />
      </XStack>

      <XStack gap="$4" flexWrap="wrap" alignItems="flex-start">
        <YStack flex={1} minWidth={320} gap="$3">
          <MetadataPanel
            doc={doc}
            tags={tagsList?.results ?? []}
            correspondents={correspondentsList?.results ?? []}
            documentTypes={documentTypesList?.results ?? []}
          />
        </YStack>
        <YStack flex={2} minWidth={400} gap="$2">
          <Text fontSize="$3" fontWeight="600" color="$color10">Vorschau</Text>
          <PdfViewer id={id} />
        </YStack>
      </XStack>
    </YStack>
  );
}

function MetadataPanel({
  doc,
  tags,
  correspondents,
  documentTypes,
}: {
  doc: PaperlessDocument;
  tags: PaperlessTag[];
  correspondents: PaperlessCorrespondent[];
  documentTypes: PaperlessDocumentType[];
}) {
  const toast = useAppToast();
  const update = useUpdateDocument(doc.id);

  function applyPatch(patch: UpdateDocumentPatch, label: string) {
    update.mutate(patch, {
      onSuccess: () => toast.showSuccess(`${label} aktualisiert`),
      onError: (err) => toast.showError(err),
    });
  }

  const correspondentName = doc.correspondent
    ? correspondents.find((c) => c.id === doc.correspondent)?.name ?? '—'
    : '—';
  const documentTypeName = doc.document_type
    ? documentTypes.find((t) => t.id === doc.document_type)?.name ?? '—'
    : '—';

  return (
    <YStack gap="$3" borderWidth={1} borderColor="$color5" borderRadius="$3" padding="$3">
      <Field label="Titel">
        <Text fontSize="$4">{doc.title || '(ohne Titel)'}</Text>
      </Field>
      <Field label="Korrespondent">
        <Text fontSize="$4">{correspondentName}</Text>
      </Field>
      <Field label="Dokumenttyp">
        <Text fontSize="$4">{documentTypeName}</Text>
      </Field>
      <Field label="Erstellt">
        <Text fontSize="$4">{formatDate(doc.created)}</Text>
      </Field>
      <Field label="Hinzugefügt">
        <Text fontSize="$4">{formatDate(doc.added)}</Text>
      </Field>

      <Separator />

      <Field label="Tags">
        <TagEditor
          docId={doc.id}
          currentTagIds={doc.tags}
          allTags={tags}
          disabled={update.isPending}
          onChange={(next) => applyPatch({ tags: next }, 'Tags')}
        />
      </Field>
    </YStack>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <YStack gap="$1">
      <Text fontSize="$2" color="$color10">{label}</Text>
      {children}
    </YStack>
  );
}

function TagEditor({
  currentTagIds,
  allTags,
  disabled,
  onChange,
}: {
  docId: number;
  currentTagIds: number[];
  allTags: PaperlessTag[];
  disabled: boolean;
  onChange: (next: number[]) => void;
}) {
  const currentSet = useMemo(() => new Set(currentTagIds), [currentTagIds]);
  const available = allTags.filter((t) => !currentSet.has(t.id));
  const [addId, setAddId] = useState<string>('');

  function remove(id: number) {
    onChange(currentTagIds.filter((t) => t !== id));
  }
  function add(id: number) {
    if (currentSet.has(id)) return;
    onChange([...currentTagIds, id]);
    setAddId('');
  }

  return (
    <YStack gap="$2">
      <XStack gap="$2" flexWrap="wrap">
        {currentTagIds.length === 0 ? (
          <Text fontSize="$2" color="$color10">Keine Tags</Text>
        ) : (
          currentTagIds.map((id) => {
            const tag = allTags.find((t) => t.id === id);
            return (
              <Button
                key={id}
                size="$2"
                backgroundColor="$blue5"
                borderColor="$blue8"
                borderWidth={1}
                borderRadius="$10"
                disabled={disabled}
                onPress={() => remove(id)}
              >
                {tag?.name ?? `#${id}`} ✕
              </Button>
            );
          })
        )}
      </XStack>
      {available.length > 0 ? (
        <XStack gap="$2" alignItems="center">
          <Select
            value={addId}
            onValueChange={(v) => {
              if (v) add(Number(v));
            }}
            disablePreventBodyScroll
          >
            <Select.Trigger width={220} disabled={disabled}>
              <Select.Value placeholder="+ Tag hinzufügen" />
            </Select.Trigger>
            <Select.Content zIndex={200000}>
              <Select.Viewport>
                {available.map((tag, idx) => (
                  <Select.Item key={tag.id} index={idx} value={String(tag.id)}>
                    <Select.ItemText>{tag.name}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select>
        </XStack>
      ) : null}
    </YStack>
  );
}

function PdfViewer({ id }: { id: number }) {
  const { data: blob, isLoading, error } = useDocumentPreviewBlob(id);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (isLoading) {
    return (
      <YStack alignItems="center" justifyContent="center" padding="$6" minHeight={400}>
        <Spinner size="large" />
      </YStack>
    );
  }
  if (error) {
    return (
      <Paragraph color="$red10">
        {error instanceof Error ? error.message : 'Vorschau konnte nicht geladen werden.'}
      </Paragraph>
    );
  }
  if (!objectUrl) return null;

  return (
    <iframe
      title="PDF-Vorschau"
      src={objectUrl}
      style={{
        width: '100%',
        height: '80vh',
        border: '1px solid var(--color5, #ccc)',
        borderRadius: 4,
      }}
    />
  );
}

function DownloadButton({ id, filename }: { id: number; filename: string }) {
  const api = useDocumentsApi();
  const toast = useAppToast();
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    setBusy(true);
    try {
      const blob = await api.downloadBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `document-${id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.showError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button onPress={handleDownload} disabled={busy}>
      {busy ? 'Lädt…' : 'Download'}
    </Button>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { dateStyle: 'medium' });
}
